import React, { useEffect } from "react";

const LOGO_IMAGE = "/assates/Logo__newimage-removebg-preview.png";

// Parses "YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss" or ISO strings safely (Safari-friendly).
export const parseDate = (value) => {
    if (!value) return null;
    const str = String(value).trim();
    let d = new Date(str);
    if (isNaN(d.getTime())) d = new Date(str.replace(" ", "T"));
    return isNaN(d.getTime()) ? null : d;
};

export const formatDate = (value) => {
    const d = parseDate(value);
    if (!d) return value || "";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const STATUS_STYLES = {
    Open: "bg-green-100 text-green-800",
    Upcoming: "bg-blue-100 text-blue-800",
    Close: "bg-gray-200 text-gray-700",
};

const STATUS_LABELS = {
    Open: "Open",
    Upcoming: "Upcoming",
    Close: "Closed",
};

export const StatusBadge = ({ status }) => {
    if (!status) return null;
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                STATUS_STYLES[status] || "bg-gray-100 text-gray-700"
            }`}
        >
            {STATUS_LABELS[status] || status}
        </span>
    );
};

const toExternalUrl = (link) => {
    if (!link) return "";
    const trimmed = link.trim();
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const Row = ({ label, value }) => {
    if (value === undefined || value === null || String(value).trim() === "") return null;
    return (
        <div className="flex flex-col sm:flex-row sm:gap-4 py-2 border-b border-gray-100 last:border-b-0">
            <dt className="sm:w-48 shrink-0 text-sm font-semibold text-gray-600">{label}</dt>
            <dd className="text-sm text-gray-900 break-words">{value}</dd>
        </div>
    );
};

const Section = ({ title, children }) => {
    // Hide the whole section when none of its rows has a value
    const hasContent = React.Children.toArray(children).some(
        (child) => child?.props && String(child.props.value ?? "").trim() !== ""
    );
    if (!hasContent) return null;
    return (
        <div className="mb-4">
            <h4 className="text-sm font-bold uppercase tracking-wide text-[#28282B] mb-1">{title}</h4>
            <dl>{children}</dl>
        </div>
    );
};

const ageRange = (min, max) => {
    if (min && max) return `${min} – ${max} years`;
    if (min) return `Minimum ${min} years`;
    if (max) return `Maximum ${max} years`;
    return "";
};

/**
 * Full-detail popup for an exam alert.
 * `alert`       – exam notification object (fields from examNotificationModel)
 * `fallback`    – optional { title, description, image, Created_date } for plain notifications
 */
const ExamAlertDetailModal = ({ alert, fallback, onClose }) => {
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    const isExam = alert && alert._id;
    const title = isExam ? alert.course_or_exam || fallback?.title || "Exam Alert" : fallback?.title || "Notification";
    const description = isExam ? alert.description : fallback?.description;
    const image = (isExam && alert.image) || fallback?.image;
    const createdDate = (isExam && alert.Created_date) || fallback?.Created_date;
    const classNames = isExam && Array.isArray(alert.classInfo)
        ? alert.classInfo.map((c) => c.name).filter(Boolean).join(", ")
        : "";

    return (
        <div
            className="fixed inset-0 z-[1050] flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="alert-detail-title"
        >
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 p-4 border-b">
                    <div className="flex items-center gap-3 min-w-0">
                        <img
                            src={image ? String(image).replace(/\\/g, "/") : LOGO_IMAGE}
                            alt=""
                            className="w-14 h-14 rounded-lg object-cover shrink-0 bg-gray-100"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = LOGO_IMAGE;
                            }}
                        />
                        <div className="min-w-0">
                            <h3 id="alert-detail-title" className="text-lg font-bold text-[#28282B] break-words m-0">
                                {title}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                {isExam && <StatusBadge status={alert.status} />}
                                {createdDate && (
                                    <span className="text-xs text-gray-500">Posted on {formatDate(createdDate)}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-2xl leading-none text-gray-500 hover:text-gray-900 px-2"
                    >
                        <i className="ri-close-line"></i>
                    </button>
                </div>

                {/* Body */}
                <div className="p-4 overflow-y-auto">
                    {description && <p className="text-sm text-gray-700 whitespace-pre-line mb-4">{description}</p>}

                    {isExam && (
                        <>
                            <Section title="Important Dates">
                                <Row label="Application Starts" value={formatDate(alert.start_application)} />
                                <Row label="Application Ends" value={formatDate(alert.end_application)} />
                                <Row label="Examination Date" value={formatDate(alert.examination_date)} />
                            </Section>

                            <Section title="Exam Details">
                                <Row label="Category" value={alert.catIdInfo?.name} />
                                <Row label="Conducted By" value={alert.conducted_by} />
                                <Row label="Exam / Interview" value={alert.exam_or_interview} />
                                <Row label="Post" value={alert.post} />
                                <Row label="Company" value={alert.company} />
                                <Row
                                    label="Exam For"
                                    value={
                                        alert.examFor === "States" && alert.stateIdInfo?.name
                                            ? `${alert.stateIdInfo.name}`
                                            : alert.examFor
                                    }
                                />
                                <Row label="Classes" value={classNames} />
                                <Row label="Result" value={alert.result} />
                            </Section>

                            <Section title="Eligibility">
                                <Row label="Appearing Candidates" value={alert.appearingCandidates} />
                                <Row label="Gender" value={alert.gender} />
                                <Row label="Age (General)" value={ageRange(alert.general_min_age, alert.general_max_age)} />
                                <Row
                                    label="Age (SC/ST/OBC)"
                                    value={ageRange(alert.except_general_min_age, alert.except_general_max_age)}
                                />
                                <Row label="Height & Chest" value={alert.height_and_chest} />
                            </Section>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 p-4 border-t">
                    {isExam && alert.link && (
                        <a
                            href={toExternalUrl(alert.link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 rounded-lg bg-[#28282B] text-white text-sm font-semibold no-underline hover:opacity-90"
                        >
                            Visit Official Website
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExamAlertDetailModal;
